/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AxiosError } from "axios";
import { strings } from "../../../../common/Strings/Strings";
import { ReservationTypeRequest } from "../../../../models/reservationtype/ReservationTypeRequest";
import { getGeneralError } from "../../../../util/helperFunctions";
import CloseButton from "../../../../components/CloseButton";
import AlertBox from "../../../../components/AlertBox";
import { BackendFieldError } from "../../../../models/BackendFieldError";
import { ClinicEmploymentType } from "../../../../models/employment/ClinicEmploymentType";
import Button from "../../../../components/Button";
import ReservationTypeForm, {
  ReservationTypeDataForm,
} from "./ReservationTypeForm";
import Modal from "../../../../components/Modal/Modal";

interface Props {
  close: () => void;
  isOpen: boolean;
  onSubmit: (
    reservationType: ReservationTypeRequest,
    onSuccess: () => void,
    onFailure: (error: AxiosError<any>) => void
  ) => void;
}

type AddReservationTypeFormKeys = keyof ReservationTypeDataForm;

const AddReservationTypeForm: React.FC<Props> = ({
  close,
  onSubmit,
  isOpen,
}: Props) => {
  const [isBeingAdded, setIsBeingAdded] = useState<boolean>(false);
  const [externalError, setExternalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Array<BackendFieldError<AddReservationTypeFormKeys>>
  >([]);

  const formMethods = useForm<ReservationTypeDataForm>({
    mode: "onChange",
    defaultValues: {
      color: "#bbb8c5",
      isReservableByOwners: false,
    },
  });

  const {
    handleSubmit,
    getValues,
    setError,
    reset,
    formState: { isValid },
  } = formMethods;

  const [employmentTypes, setEmploymentTypes] = useState<
    ClinicEmploymentType[]
  >([ClinicEmploymentType.VET]);
  
  const onEmploymentTypeChange = () => {
    const formValues = getValues();
    setEmploymentTypes([
      ...(formValues.allowedForVets ? [ClinicEmploymentType.VET] : []),
      ...(formValues.allowedForAssistants
        ? [ClinicEmploymentType.ASSISTANT]
        : []),
      ...(formValues.allowedForAuthorizedAssistants
        ? [ClinicEmploymentType.AUTHORIZED_ASSISTANT]
        : []),
    ]);
  };

  useEffect(() => {
    fieldErrors.forEach(
      ({ field, message }: BackendFieldError<AddReservationTypeFormKeys>) => {
        setError(field, { type: "manual", message });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors]);

  const onCancel = () => {
    reset();
    close();
  };

  const onSuccess = () => {
    setIsBeingAdded(false);
    onCancel();
  };

  const onFailure = async (err: AxiosError<any>) => {
    setIsBeingAdded(false);
    setExternalError(await getGeneralError(err));
    setFieldErrors(err.response?.data || []);
  };

  const submit = (f: ReservationTypeDataForm) => {
    const request: ReservationTypeRequest = {
      name: f.name,
      minutes: f.minutes,
      color: f.color,
      employmentTypes,
      isReservableByOwners: f.isReservableByOwners,
      isVideoConsultationAllowed: f.isVideoConsultationAllowed,
    };
    onSubmit(request, onSuccess, onFailure);
    setIsBeingAdded(true);
  };

  return (
    <Modal handleClose={onCancel} show={isOpen}>
      <Modal.Header title={strings.addReservationType} />
      <Modal.Body>
        <FormProvider {...formMethods}>
          <form id="newReservationTypeForm">
            <ReservationTypeForm
              onEmploymentTypeChange={onEmploymentTypeChange}
            />
            <AlertBox
              message={
                employmentTypes.length === 0
                  ? strings.chooseAnEmploymentType
                  : ""
              }
              className="m-3"
            />
          </form>
          <AlertBox message={externalError} />
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="modal-main-button"
          disabled={!isValid}
          form="newReservationTypeForm"
          fullWidth={false}
          loading={isBeingAdded}
          onClick={handleSubmit(submit)}
        >
          {strings.add}
        </Button>
        <CloseButton onClick={onCancel} text={strings.cancel} />
      </Modal.Footer>
    </Modal>
  );
};

export default AddReservationTypeForm;
